import type { Observable } from 'rxjs';
import type { TokenResponse } from './token.service';

/**
 * Almacén **de proceso** del client token en SSR.
 *
 * Vive a nivel de módulo, fuera de la inyección de dependencias: Node carga el
 * bundle del servidor una sola vez y cada request de SSR crea un inyector raíz
 * nuevo, así que un `TokenService` por request negociaría un token por página
 * (incidente del 16/09/2026: 397 `POST /oauth/token` en 8 minutos). Este objeto,
 * en cambio, dura lo que dura el proceso Node.
 *
 * Compartirlo entre requests es seguro porque el client token es un token de
 * **aplicación** (`grant_type=client_credentials`): no identifica a ningún
 * usuario ni lleva datos de sesión, y vale lo mismo para cualquier visitante.
 * Los tokens de usuario y de carrito, en cambio, sí son de un visitante
 * concreto: siguen siendo browser-only y **nunca** deben pasar por aquí.
 *
 * Solo contiene datos derivados de la respuesta de `/oauth/token`; nada de la
 * request entrante (cookies, cabeceras, usuario, carrito). `Object.seal` impide
 * añadirle claves en runtime. Solo lo lee y escribe `TokenService` en servidor.
 */
export interface SsrClientTokenState {
  /** Access token de aplicación vigente, o `null` si hay que negociar. */
  token: string | null;
  /** Instante (ms epoch) en que caduca `token`. */
  expiresAt: number;
  /** Negociación en curso, compartida por todas las requests que esperan. */
  inFlight: Observable<TokenResponse> | null;
  /** Instante (ms epoch) del último fallo de `/oauth/token`; 0 si el último intento fue bien. */
  lastFailureAt: number;
}

/** El token se da por caducado este margen antes de `expiresAt`. */
export const SSR_CLIENT_TOKEN_RENEW_MARGIN_MS = 60_000;

/** Tras un fallo de `/oauth/token`, no se vuelve a pedir hasta pasado este tiempo. */
export const SSR_CLIENT_TOKEN_FAILURE_BACKOFF_MS = 3_000;

/**
 * Una negociación de /oauth/token que tarda más que esto se da por fallida (y
 * activa el backoff). Todas las requests del proceso esperan la misma
 * negociación: sin límite, un POST colgado bloquearía el SSR entero hasta el
 * timeout de undici (300 s).
 */
export const SSR_CLIENT_TOKEN_TIMEOUT_MS = 10_000;

export const ssrClientTokenStore: SsrClientTokenState = Object.seal({
  token: null,
  expiresAt: 0,
  inFlight: null,
  lastFailureAt: 0,
});

/**
 * Devuelve el almacén a su estado inicial.
 * @internal Solo para tests: el código de la app nunca debe llamarlo.
 */
export function resetSsrClientTokenStore(): void {
  ssrClientTokenStore.token = null;
  ssrClientTokenStore.expiresAt = 0;
  ssrClientTokenStore.inFlight = null;
  ssrClientTokenStore.lastFailureAt = 0;
}
